import { Check, FileCheck02, Mail01, XClose } from '@untitledui/icons';
import React, { useCallback } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import type {
  ProposalDetail,
  ProposalResolution,
} from '@/lib/proposals/core/types';
import { useProposalOptional } from '@/lib/proposals/hooks/useProposal';

import type { ComponentRenderProps } from '../../../types/dynamic_views';

/**
 * Derive the CTA button state from proposal resolution.
 *
 * - No proposal yet → "Draft Email" (compose a new one)
 * - Pending (or still loading) → "Open Draft" (view/edit the draft)
 * - Approved → "Sent" (non-actionable, green)
 * - Rejected/failed/withdrawn → "Rejected" (non-actionable, muted red)
 */
type ButtonState = 'draft' | 'open' | 'sent' | 'rejected';

function deriveButtonState(
  proposalId: number | undefined,
  resolution: ProposalResolution | undefined,
  isLoading: boolean
): ButtonState {
  if (proposalId === undefined) {
    return 'draft';
  }
  if (isLoading || resolution === undefined || resolution === 'pending') {
    return 'open';
  }
  if (resolution === 'approved') {
    return 'sent';
  }
  // rejected, failed, withdrawn
  return 'rejected';
}

export const ConversationStarterComponent: React.FC<ComponentRenderProps> = ({
  component,
  onInteraction,
  className = '',
  /**
   * Whether a proposal creation is in progress. When true, the Draft Email button
   * is disabled and shows loading state to prevent double-submission.
   * @default false (not composing)
   */
  isComposing,
}) => {
  const { currentOrg } = useOrg();
  const { props } = component;

  // Extract variables with safe defaults BEFORE first early return
  const validProps = props && typeof props === 'object';
  const context_label =
    validProps &&
    typeof props.context_label === 'string' &&
    props.context_label.trim()
      ? props.context_label
      : '';
  const subject =
    validProps && typeof props.subject === 'string' && props.subject.trim()
      ? props.subject
      : '';
  const body =
    validProps && typeof props.body === 'string' && props.body.trim()
      ? props.body
      : '';
  const hasRequiredFields = !!context_label && !!subject && !!body;
  const proposal_id =
    validProps && hasRequiredFields && typeof props.proposal_id === 'number'
      ? props.proposal_id
      : undefined;
  const source_refs = validProps ? props.source_refs : undefined;

  // Fetch proposal resolution when a proposal_id exists and org context is loaded
  const orgId = currentOrg?.id;
  const { data: proposal, isLoading: proposalLoading } = useProposalOptional(
    orgId ?? 0,
    orgId && proposal_id ? proposal_id : undefined
  ) as { data: ProposalDetail | undefined; isLoading: boolean };

  const handleDraftEmail = useCallback(() => {
    if (onInteraction) {
      onInteraction('compose_email', {
        component_id: component.id,
        context_label,
        subject,
        body,
      });
    }
  }, [onInteraction, component.id, context_label, subject, body]);

  const handleOpenDraft = useCallback(() => {
    if (onInteraction && proposal_id) {
      onInteraction('open_proposal', {
        proposal_id,
      });
    }
  }, [onInteraction, proposal_id]);

  // Runtime validation before type narrowing - early return AFTER all hooks
  if (!validProps) {
    console.error('ConversationStarterComponent: Invalid props object', {
      component_id: component.id,
      props_type: typeof props,
    });
    return <div>Invalid component props</div>;
  }

  // Validate required fields
  if (!context_label || !subject || !body) {
    console.error('ConversationStarterComponent: Missing required fields', {
      component_id: component.id,
      has_context_label: !!context_label,
      has_subject: !!subject,
      has_body: !!body,
      received_types: {
        context_label: typeof props?.context_label,
        subject: typeof props?.subject,
        body: typeof props?.body,
      },
    });
    return <div>Missing required fields</div>;
  }

  const buttonState = deriveButtonState(
    proposal_id,
    proposal?.resolution,
    proposalLoading
  );

  return (
    <div
      className={`border border-border-secondary rounded-lg p-4 space-y-3 transition-colors hover:border-border-primary ${className}`}
      data-component-id={component.id}
      role='article'
      aria-label={`Conversation starter: ${context_label}`}
    >
      {/* Context label row with optional source refs */}
      <div className='text-xs font-medium uppercase tracking-wide text-tertiary'>
        {context_label}
        {(() => {
          if (!Array.isArray(source_refs)) {
            return null;
          }
          if (!source_refs.every(ref => typeof ref === 'number')) {
            console.warn(
              'ConversationStarterComponent: Invalid source_refs elements',
              {
                component_id: component.id,
                received_types: source_refs.map(ref => typeof ref),
              }
            );
            return null;
          }
          return (
            source_refs.length > 0 && (
              <span className='ml-1'>[{source_refs.join(', ')}]</span>
            )
          );
        })()}
      </div>

      {/* Subject line */}
      <div className='text-sm font-semibold text-fg-primary'>{subject}</div>

      {/* Body preview */}
      <div className='text-sm text-fg-secondary line-clamp-3 whitespace-pre-line'>
        {body}
      </div>

      {/* CTA button row - only show if onInteraction is provided */}
      {onInteraction && (
        <div className='flex justify-end'>
          {buttonState === 'draft' && (
            <Button
              color='secondary'
              size='sm'
              iconLeading={Mail01}
              onClick={handleDraftEmail}
              isDisabled={isComposing}
              isLoading={isComposing}
              aria-label={`Draft email about ${context_label}`}
              aria-busy={isComposing}
            >
              Draft email
            </Button>
          )}
          {buttonState === 'open' && (
            <Button
              color='secondary'
              size='sm'
              iconLeading={FileCheck02}
              onClick={handleOpenDraft}
              aria-label={`Open existing draft about ${context_label}`}
            >
              Open draft
            </Button>
          )}
          {buttonState === 'sent' && (
            <span
              className='inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium bg-utility-success-50 text-utility-success-700 dark:bg-utility-success-950 dark:text-utility-success-300'
              aria-label={`Email about ${context_label} was sent`}
            >
              <Check className='size-4 flex-shrink-0' strokeWidth={2.5} />
              Sent
            </span>
          )}
          {buttonState === 'rejected' && (
            <span
              className='inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium bg-utility-error-50 text-utility-error-700 dark:bg-utility-error-950 dark:text-utility-error-300'
              aria-label={`Email draft about ${context_label} was rejected`}
            >
              <XClose className='size-4 flex-shrink-0' strokeWidth={2.5} />
              Rejected
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationStarterComponent;
